// @flow
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import type { ExclusionInfo } from 'state/types';
import localizedStrings from 'util/localizedStrings';
import logger from 'portfolio-shared/util/logging';
import { setExclusionInfo } from 'state/commands/catalogCommands';
import { actions as modalPromptActions } from 'portfolio-shared/state/reducers/modalPromptReducer';

const { showModalPrompt } = modalPromptActions;

type Props = {
  catalogId: string,
  exclusionInfo: ?ExclusionInfo,
  intl: IntlShape,
  setExclusionInfo: typeof setExclusionInfo,
  showModalPrompt: typeof showModalPrompt,
};

type State = {
  exclusionPredicate: string,
  exclusionParam: string,
  isLoading: boolean,
  selectedExclusion: number,
};

const exclusionPredicates = ['startsWith', 'endsWith', 'contains'];

class FileAndFolderExclusions extends Component<Props, State> {
  handleChangeParam: Function;
  handleChangePredicate: Function;
  handleChangeSelected: Function;
  handleClickAdd: Function;
  handleListKeyDown: Function;
  handleParamKeyDown: Function;

  state = {
    exclusionPredicate: 'startsWith',
    exclusionParam: '',
    isLoading: false,
    selectedExclusion: 0,
  };

  constructor() {
    super();

    this.handleChangeParam = this.handleChangeParam.bind(this);
    this.handleChangePredicate = this.handleChangePredicate.bind(this);
    this.handleChangeSelected = this.handleChangeSelected.bind(this);
    this.handleClickAdd = this.handleClickAdd.bind(this);
    this.handleListKeyDown = this.handleListKeyDown.bind(this);
    this.handleParamKeyDown = this.handleParamKeyDown.bind(this);
  }

  handleChangeSelected(e: SyntheticEvent<HTMLSelectElement>) {
    this.setState({ selectedExclusion: parseInt(e.currentTarget.value, 10) });
  }

  handleChangePredicate(e: SyntheticEvent<HTMLSelectElement>) {
    this.setState({ exclusionPredicate: e.currentTarget.value });
  }

  handleChangeParam(e: SyntheticEvent<HTMLInputElement>) {
    this.setState({ exclusionParam: e.currentTarget.value });
  }

  handleListKeyDown(e: SyntheticKeyboardEvent<HTMLSelectElement>) {
    const { catalogId, exclusionInfo } = this.props;

    const selectedIndices = [...e.currentTarget.options].reduce(
      (acc, o, idx) => (o.selected && acc.concat(idx)) || acc,
      []
    );

    if (
      ['Backspace', 'Delete'].includes(e.key) &&
      exclusionInfo &&
      Array.isArray(exclusionInfo.pathExclusions) &&
      selectedIndices.length
    ) {
      logger.debug(
        'Delete ',
        selectedIndices.map(i => exclusionInfo.pathExclusions[i])
      );
      this.props.showModalPrompt({
        isButton2Default: true,
        message:
          localizedStrings.CATALOG_OPTIONS_REMOVE_EXCLUSIONS_CONFIRMATION,
        onButton2: async () => {
          this.setState({ isLoading: true });
          await this.props.setExclusionInfo(catalogId, {
            ...exclusionInfo,
            pathExclusions: exclusionInfo.pathExclusions.filter(
              (ex, idx) => !selectedIndices.includes(idx)
            ),
          });
          this.setState({ isLoading: false });
        },
      });
    }
  }

  handleParamKeyDown(e: SyntheticKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      this.handleClickAdd(e);
    }
  }

  async handleClickAdd(e: SyntheticEvent<>) {
    const { catalogId, exclusionInfo } = this.props;
    const { exclusionParam, exclusionPredicate } = this.state;

    if (exclusionParam && exclusionInfo) {
      logger.debug(`Add exclusion: {${exclusionPredicate}, ${exclusionParam}}`);
      this.setState({ exclusionParam: '', isLoading: true });
      await this.props.setExclusionInfo(catalogId, {
        ...exclusionInfo,
        pathExclusions: [
          ...exclusionInfo.pathExclusions,
          {
            name: exclusionPredicate,
            value: exclusionParam,
          },
        ],
      });
      this.setState({ isLoading: false });
    }
  }

  render() {
    const {
      exclusionInfo,
      intl: { formatMessage },
    } = this.props;

    const { exclusionPredicate, exclusionParam, isLoading } = this.state;

    return (
      <section className="box">
        <h2 className="subtitle">
          <FormattedMessage
            {...localizedStrings.CATALOG_OPTIONS_EXCLUSION_LABEL}
          />
        </h2>
        <div className="field">
          <div className="control">
            <div className="select is-multiple is-small">
              <select
                multiple
                onChange={this.handleChangeSelected}
                onKeyDown={this.handleListKeyDown}
                size="8"
                style={{ width: '400px' }}
                data-test-name="file-exclusions"
              >
                {exclusionInfo &&
                  exclusionInfo.pathExclusions &&
                  exclusionInfo.pathExclusions.map((ex, idx) => (
                    <option key={idx} value={idx} data-test-name="exclusion">
                      {`${formatMessage(localizedStrings[ex.name])} '${
                        ex.value
                      }'`}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="vertical-spacer-small" />
          <div className="field has-addons">
            <div className="control">
              <div className="select">
                <select
                  onChange={this.handleChangePredicate}
                  value={exclusionPredicate}
                  data-test-name="new-exclusion-predicate"
                >
                  {exclusionPredicates.map(t => (
                    <option key={t} value={t} data-test-name="predicate">
                      {formatMessage(localizedStrings[t])}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="control">
              <input
                className="input"
                type="text"
                onChange={this.handleChangeParam}
                onKeyDown={this.handleParamKeyDown}
                value={exclusionParam}
                data-test-name="new-exclusion-def"
              />
            </div>
            <div className="control">
              <button
                className={`button ${isLoading ? 'is-loading' : ''}`}
                disabled={!exclusionParam || isLoading}
                onClick={this.handleClickAdd}
                data-test-name="add-exclusion"
              >
                <span className="icon is-small">
                  <i className="fa fa-plus" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default injectIntl(FileAndFolderExclusions);



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/FileAndFolderExclusions.js